import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { WorkflowProgress } from "@/components/WorkflowProgress";
import { CoverageMap } from "@/components/CoverageMap";
import { ClarifyStep } from "@/components/ClarifyStep";
import { EvaluateStep } from "@/components/EvaluateStep";
import { PromptResults } from "@/components/PromptResults";
import { ExportStep } from "@/components/ExportStep";
import { RefinementPanel } from "@/components/RefinementPanel";
import { VersionTimeline } from "@/components/VersionTimeline";
import { PullLibraryDialog } from "@/features/project/PullLibraryDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import API from "@/lib/api";
import { DialogBody } from "@/components/shared/DialogBody";
import {
  Zap, Trash2, Clock, ChevronRight, ChevronDown, Loader2,
  Target, Pencil, Wrench, ClipboardCheck, Gauge,
  FileOutput, History, Library, Filter, Sparkles
} from "lucide-react";
import { toast, Toaster } from "sonner";

const PLATFORMS = [
  { value: "emergent", label: "Emergent" },
  { value: "vibe-code", label: "Vibe-Code Platforms" },
  { value: "ai-assistants", label: "AI Coding Assistants" },
  { value: "no-code", label: "No-Code Builders" },
  { value: "product-planning", label: "Product Planning" },
  { value: "general", label: "General" },
];

const OUTPUT_MODES = [
  { value: "concise", label: "Concise" },
  { value: "standard", label: "Standard" },
  { value: "advanced", label: "Advanced" },
];

const PROMPT_MODES = [
  { id: "define",    label: "Define",    icon: Target },
  { id: "shape",     label: "Shape",     icon: Pencil },
  { id: "implement", label: "Implement", icon: Wrench },
  { id: "verify",    label: "Verify",    icon: ClipboardCheck },
  { id: "iterate",   label: "Iterate",   icon: Gauge },
];

const FILTER_TAGS = [
  "Product", "UX", "UI", "Frontend", "Backend", "Business Logic",
  "Data", "API", "Integrations", "Authentication & Authorization",
  "Security", "Error Handling", "Performance", "Testing", "State Management",
];

function LoadingState({ messages }) {
  const [msgIdx, setMsgIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => { const t = setInterval(() => setElapsed((s) => s + 1), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { if (messages.length <= 1) return; const t = setInterval(() => setMsgIdx((i) => (i + 1) % messages.length), 4000); return () => clearInterval(t); }, [messages.length]);
  return (
    <div className="max-w-3xl mx-auto border border-[var(--pf-border)] p-12 text-center" data-testid="loading-state">
      <Loader2 className="w-8 h-8 animate-spin text-[var(--pf-accent)] mx-auto mb-3" />
      <h3 className="font-heading text-lg font-bold mb-1 text-[var(--pf-text)]">{messages[msgIdx]}</h3>
      <p className="text-xs text-[var(--pf-text-muted)] mt-2">{elapsed}s elapsed</p>
    </div>
  );
}

export default function ProjectWorkspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [standardBlocks, setStandardBlocks] = useState(null);

  // Library browse state — dialog owns its internals; parent only gates visibility
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Versions sheet
  const [versionsSheetOpen, setVersionsSheetOpen] = useState(false);

  // Form state
  const [concept, setConcept] = useState("");
  const [featureName, setFeatureName] = useState("");
  const [targetPlatform, setTargetPlatform] = useState("general");
  const [outputMode, setOutputMode] = useState("standard");

  // Navigation state
  const [activeMode, setActiveMode] = useState("define");
  const [activeFilters, setActiveFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Workflow state
  const [workflowStep, setWorkflowStep] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [enrichedConcept, setEnrichedConcept] = useState("");
  const [clarificationData, setClarificationData] = useState(null);
  const [generatingModes, setGeneratingModes] = useState({});
  const [versionKey, setVersionKey] = useState(0);

  const loadProject = useCallback(async () => {
    try {
      const [projRes, promptsRes, settingsRes] = await Promise.all([
        API.get(`/projects/${projectId}`),
        API.get(`/prompts/project/${projectId}`),
        API.get("/settings").catch(() => ({ data: {} })),
      ]);
      setProject(projRes.data);
      setPrompts(promptsRes.data);
      const s = settingsRes.data;
      if (s.default_platform) setTargetPlatform(s.default_platform);
      if (s.default_prompt_length) setOutputMode(s.default_prompt_length);
      if (promptsRes.data.length > 0 && !selectedPrompt) {
        const firstPrompt = promptsRes.data[0];
        setSelectedPrompt(firstPrompt);
        const savedStep = firstPrompt.workflow_step;
        const hasBlocks = Object.values(firstPrompt.blocks || {}).some((b) => b.length > 0);
        if (hasBlocks) {
          setWorkflowStep(null);
        } else if (savedStep === "evaluate" && firstPrompt.master_prompt) {
          setWorkflowStep("evaluate");
          setCompletedSteps(["clarify"]);
        } else {
          setWorkflowStep(null);
        }
      }
    } catch (e) {
      console.error("Load project error:", e);
      if (e.response?.status === 404) navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate, selectedPrompt]);

  useEffect(() => { loadProject(); }, [loadProject]);
  useEffect(() => { API.get("/prompts/standard-blocks").then(({ data }) => setStandardBlocks(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (location.state?.quickConcept && !loading) {
      setConcept(location.state.quickConcept);
      setWorkflowStep("coverage");
      window.history.replaceState({}, document.title);
    }
  }, [loading, location.state]);

  const toggleFilter = (tag) => { setActiveFilters((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]); };

  // --- Workflow handlers ---
  const startWorkflow = () => { if (!concept.trim()) return; setWorkflowStep("coverage"); setCompletedSteps([]); setEnrichedConcept(""); };

  const handleCoverageAction = async ({ pullModes, generateModes, allSelected }) => {
    // Step 1: Create the prompt doc via direct generate (fast, no clarify/evaluate)
    setGenerating(true);
    setWorkflowStep("blocks");
    try {
      const { data: promptDoc } = await API.post("/prompts/generate", {
        project_id: projectId, concept: concept.trim(), feature_name: featureName.trim(),
        target_platform: targetPlatform, output_mode: outputMode, categories: [], phases: [],
      });
      setPrompts((prev) => [promptDoc, ...prev]);
      setSelectedPrompt(promptDoc);
      setGenerating(false);

      // Step 2: Pull library blocks for covered modes
      if (pullModes.length > 0) {
        try {
          const { data: libData } = await API.get("/library", { params: { limit: 200 } });
          const blocks = libData.items || [];
          const relevantIds = blocks.filter((b) => pullModes.includes(b.mode)).map((b) => b.id).filter(Boolean);
          if (relevantIds.length > 0) {
            const { data: pullResult } = await API.post(`/prompts/${promptDoc.id}/pull-blocks`, { library_block_ids: relevantIds });
            setSelectedPrompt(pullResult.prompt);
            setPrompts((prev) => prev.map((p) => (p.id === pullResult.prompt.id ? pullResult.prompt : p)));
            toast.success(`Pulled ${pullResult.added} library blocks`);
          }
        } catch (e) {
          console.error("Pull error:", e);
        }
      }

      // Step 3: Generate blocks for gap modes
      if (generateModes.length > 0) {
        const modeProgress = {};
        generateModes.forEach((m) => { modeProgress[m] = "loading"; });
        setGeneratingModes({ ...modeProgress });
        toast.success("Generating blocks", { description: `Building blocks for ${generateModes.length} mode${generateModes.length > 1 ? "s" : ""}...` });

        const BATCH_SIZE = 2;
        const generateMode = async (mode) => {
          try {
            const { data } = await API.post(`/prompts/${promptDoc.id}/generate-mode`, { mode });
            setGeneratingModes((prev) => ({ ...prev, [mode]: "done" }));
            setSelectedPrompt((prev) => prev?.id === promptDoc.id ? { ...prev, blocks: { ...prev.blocks, [mode]: data.blocks } } : prev);
            setPrompts((prev) => prev.map((p) => p.id === promptDoc.id ? { ...p, blocks: { ...p.blocks, [mode]: data.blocks } } : p));
            return data.blocks.length;
          } catch (err) {
            setGeneratingModes((prev) => ({ ...prev, [mode]: "error" }));
            return 0;
          }
        };

        for (let i = 0; i < generateModes.length; i += BATCH_SIZE) {
          const batch = generateModes.slice(i, i + BATCH_SIZE);
          await Promise.all(batch.map(generateMode));
        }
        setGeneratingModes({});
      }

      setCompletedSteps(["coverage", "blocks"]);
      toast.success("Playbook ready", { description: "Library blocks pulled + new blocks generated." });
    } catch (err) {
      console.error(err);
      toast.error("Failed to set up playbook");
      setGenerating(false);
      setWorkflowStep("coverage");
    }
  };

  const handleCoverageSkip = () => {
    setWorkflowStep(null);
  };

  const handleClarifyComplete = (finalConcept, clarification) => {
    setEnrichedConcept(finalConcept);
    setClarificationData(clarification);
    setCompletedSteps((prev) => [...prev.filter((s) => s !== "clarify"), "clarify"]);
    generateMasterPrompt(finalConcept, clarification);
  };

  const generateMasterPrompt = async (finalConcept, clarification) => {
    setWorkflowStep("evaluate");
    setGenerating(true);
    try {
      const { data: promptDoc } = await API.post("/prompts/master-prompt", {
        project_id: projectId, enriched_concept: finalConcept, target_platform: targetPlatform,
        output_mode: outputMode, feature_name: featureName.trim(), clarification: clarification || undefined,
      });
      setPrompts((prev) => [promptDoc, ...prev]);
      setSelectedPrompt(promptDoc);
      toast.success("Master prompt generated");
    } catch (e) {
      toast.error("Generation failed", { description: e.response?.data?.detail || "Try again." });
      setWorkflowStep("clarify");
    } finally { setGenerating(false); }
  };

  const handleEvaluateAccept = async (promptDoc) => {
    setCompletedSteps((prev) => [...prev.filter((s) => s !== "evaluate"), "evaluate"]);
    setWorkflowStep("blocks");
    await generateAllBlocks(promptDoc);
  };

  const generateAllBlocks = async (promptDoc) => {
    if (!promptDoc?.id) return;
    const modes = PROMPT_MODES.map((m) => m.id);
    const modeProgress = {};
    modes.forEach((m) => { modeProgress[m] = "loading"; });
    setGeneratingModes({ ...modeProgress });
    toast.success("Generating blocks", { description: "Building prompt blocks across 5 phases..." });

    // Batch generation in groups of 2 to avoid K8s 60s ingress timeout
    const BATCH_SIZE = 2;
    let totalBlocks = 0;
    let successCount = 0;

    const generateMode = async (mode) => {
      try {
        const { data } = await API.post(`/prompts/${promptDoc.id}/generate-mode`, { mode });
        modeProgress[mode] = "done";
        setGeneratingModes((prev) => ({ ...prev, [mode]: "done" }));
        setSelectedPrompt((prev) => prev?.id === promptDoc.id ? { ...prev, blocks: { ...prev.blocks, [mode]: data.blocks } } : prev);
        setPrompts((prev) => prev.map((p) => p.id === promptDoc.id ? { ...p, blocks: { ...p.blocks, [mode]: data.blocks } } : p));
        return data.blocks.length;
      } catch (err) {
        modeProgress[mode] = "error";
        setGeneratingModes((prev) => ({ ...prev, [mode]: "error" }));
        return 0;
      }
    };

    for (let i = 0; i < modes.length; i += BATCH_SIZE) {
      const batch = modes.slice(i, i + BATCH_SIZE);
      const results = await Promise.all(batch.map(generateMode));
      results.forEach((count) => { totalBlocks += count; if (count > 0) successCount++; });
    }

    setGeneratingModes({});
    setCompletedSteps((prev) => [...prev.filter((s) => s !== "blocks"), "blocks"]);
    if (successCount === modes.length) toast.success("All blocks generated", { description: `${totalBlocks} blocks across ${modes.length} modes` });
    else toast.warning(`${successCount}/${modes.length} modes generated`, { description: "Some modes failed. Try regenerating them individually." });
  };

  const handleDirectGenerate = async (e) => {
    if (e) e.preventDefault();
    if (!concept.trim()) return;
    setGenerating(true);
    try {
      const { data: promptDoc } = await API.post("/prompts/generate", {
        project_id: projectId, concept: concept.trim(), feature_name: featureName.trim(),
        target_platform: targetPlatform, output_mode: outputMode, categories: [], phases: [],
      });
      setPrompts((prev) => [promptDoc, ...prev]);
      setSelectedPrompt(promptDoc);
      setGenerating(false);
      setWorkflowStep(null);
      toast.success("Playbook created", { description: "Generating blocks..." });
      await generateAllBlocks(promptDoc);
    } catch (err) { console.error(err); toast.error("Generation failed"); setGenerating(false); }
  };

  const handleDeleteProject = async () => { try { await API.delete(`/projects/${projectId}`); navigate("/dashboard"); } catch (e) { console.error(e); } };
  const handleDeletePrompt = async (promptId) => {
    try { await API.delete(`/prompts/${promptId}`); setPrompts((prev) => prev.filter((p) => p.id !== promptId));
      if (selectedPrompt?.id === promptId) setSelectedPrompt(prompts.find((p) => p.id !== promptId) || null);
    } catch (e) { console.error(e); }
  };

  const handleContentSaved = (category, blockIndex, newContent, fullBlock) => {
    if (!selectedPrompt) return;
    const updated = { ...selectedPrompt, blocks: { ...(selectedPrompt.blocks || {}) } };
    const catBlocks = [...(updated.blocks[category] || [])];
    if (catBlocks[blockIndex]) { catBlocks[blockIndex] = fullBlock ? fullBlock : { ...catBlocks[blockIndex], content: newContent }; }
    updated.blocks[category] = catBlocks;
    setSelectedPrompt(updated);
    setPrompts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  };

  const handleModeBlocksReplaced = (mode, newBlocks) => {
    if (!selectedPrompt) return;
    setSelectedPrompt((prev) => prev ? { ...prev, blocks: { ...prev.blocks, [mode]: newBlocks } } : prev);
    setPrompts((prev) => prev.map((p) => p.id === selectedPrompt.id ? { ...p, blocks: { ...p.blocks, [mode]: newBlocks } } : p));
    API.get(`/prompts/project/${projectId}`).then(({ data }) => {
      const updated = data.find((p) => p.id === selectedPrompt.id);
      if (updated) { setSelectedPrompt(updated); setPrompts(data); }
    }).catch(() => {});
  };

  const handleRefined = (updatedPrompt) => { setSelectedPrompt(updatedPrompt); setPrompts((prev) => prev.map((p) => (p.id === updatedPrompt.id ? updatedPrompt : p))); setVersionKey((k) => k + 1); };
  const handleVersionRestored = (restoredPrompt) => { setSelectedPrompt(restoredPrompt); setPrompts((prev) => prev.map((p) => (p.id === restoredPrompt.id ? restoredPrompt : p))); setVersionKey((k) => k + 1); };

  // --- Library browse ---

  if (loading) return <div className="min-h-screen bg-[var(--pf-bg)]"><Header /><div className="max-w-[1400px] mx-auto px-6 py-8"><div className="skeleton-line w-48 h-8" /></div></div>;

  const hasPrompts = selectedPrompt && !generating;
  const hasBlocks = hasPrompts && Object.values(selectedPrompt.blocks || {}).some((b) => b.length > 0);
  const isInWorkflow = workflowStep !== null;

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]" data-testid="project-workspace">
      <Header projectName={project?.name} />
      <main className="max-w-[1400px] mx-auto px-6 py-3">
        {/* Compact project bar: concept + actions */}
        <div className="flex items-center gap-3 mb-3">
          {(() => {
            const displayConcept = selectedPrompt?.concept || project?.description || concept;
            if (displayConcept) {
              return <span className="text-xs text-[var(--pf-text-secondary)] truncate flex-1 font-mono" data-testid="project-concept">{displayConcept}</span>;
            }
            return <span className="text-xs text-[var(--pf-text-muted)] truncate flex-1 italic" data-testid="project-concept-empty">— No concept yet. Enter one below to begin. —</span>;
          })()}
          {!isInWorkflow && hasPrompts && selectedPrompt?.evaluation?.score && (
            <span className={`text-[10px] px-1.5 py-0.5 font-medium shrink-0 ${selectedPrompt.evaluation.score >= 70 ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"}`} data-testid="prompt-quality-badge">Score: {selectedPrompt.evaluation.score}</span>
          )}
          {!isInWorkflow && hasPrompts && (
            <>
              <button onClick={() => { setConcept(selectedPrompt?.concept || project?.description || ""); setWorkflowStep("coverage"); }}
                className="text-[10px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium shrink-0 flex items-center gap-1"
                data-testid="context-coverage-map">
                <Target className="w-3 h-3" />Coverage Map
              </button>
              {selectedPrompt?.version_count > 0 && (
                <button onClick={() => setVersionsSheetOpen(true)}
                  className="text-[10px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium shrink-0 flex items-center gap-1"
                  data-testid="context-versions">
                  <History className="w-3 h-3" />Versions (v{selectedPrompt.version_count})
                </button>
              )}
              <button onClick={() => setWorkflowStep("evaluate")} className="text-[10px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium shrink-0" data-testid="context-view-eval">Evaluation</button>
              <button onClick={() => setWorkflowStep("export")} className="text-[10px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium shrink-0" data-testid="context-go-export">Export</button>
            </>
          )}
          <Button variant="ghost" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-project-button" className="rounded-none text-red-400 hover:text-red-600 hover:bg-red-50 h-7 px-2 text-[10px] shrink-0">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>

        {/* Workflow Progress Bar */}
        {isInWorkflow && <div className="mb-3"><WorkflowProgress currentStep={workflowStep} completedSteps={completedSteps} onStepClick={(step) => { if (step === "blocks") setWorkflowStep(hasBlocks ? null : "blocks"); else setWorkflowStep(step); }} /></div>}

        {/* === WORKFLOW STEPS === */}
        {workflowStep === "coverage" && <CoverageMap concept={concept} projectId={projectId} onPullAndGenerate={handleCoverageAction} onSkip={handleCoverageSkip} />}
        {workflowStep === "clarify" && <ClarifyStep projectId={projectId} concept={concept} onConceptChange={setConcept} onComplete={handleClarifyComplete} />}
        {workflowStep === "evaluate" && (<>{generating ? <LoadingState messages={["Analyzing your concept...", "Structuring requirements...", "Drafting prompt...", "Almost there..."]} /> : selectedPrompt ? <EvaluateStep promptData={selectedPrompt} onAccept={handleEvaluateAccept} onBack={() => setWorkflowStep("clarify")} /> : null}</>)}
        {workflowStep === "export" && selectedPrompt && <ExportStep promptData={selectedPrompt} onBack={() => setWorkflowStep(hasBlocks ? null : "blocks")} />}

        {/* === WORKSPACE === */}
        {(workflowStep === "blocks" || workflowStep === null) && workflowStep !== "export" && (
          <>
            {/* INITIAL STATE — No prompt yet: centered full-width */}
            {!hasPrompts && !generating && (
              <div className="max-w-3xl mx-auto" data-testid="empty-workspace">
                <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)]">
                  <div className="border-b border-[var(--pf-border)] px-6 py-4">
                    <h2 className="font-heading text-lg font-bold flex items-center gap-2 text-[var(--pf-text)]"><Zap className="w-4 h-4 text-[var(--pf-accent)]" />What are you building?</h2>
                    <p className="text-xs text-[var(--pf-text-secondary)] mt-1">Describe your idea. We'll check what your library covers and generate the rest.</p>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">Your Concept</Label>
                      <Textarea value={concept} onChange={(e) => setConcept(e.target.value)}
                        placeholder={"Describe your product idea, feature, or system in plain English...\n\ne.g. An invoice management tool for freelancers with payment tracking, automated reminders, and client portals"} required data-testid="concept-input"
                        className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] resize-none font-mono text-sm" rows={5} />
                    </div>
                    <Button onClick={startWorkflow} disabled={!concept.trim()} data-testid="start-workflow-button"
                      className="w-full bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-11 font-medium text-sm">
                      <Zap className="w-4 h-4 mr-2" />Build Project
                    </Button>
                    <Collapsible>
                      <CollapsibleTrigger className="flex items-center justify-between w-full group py-2 border-t border-[var(--pf-border)]" data-testid="advanced-options-toggle">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--pf-text-muted)]">Advanced Options</span>
                        <ChevronRight className="w-3 h-3 text-[var(--pf-text-muted)] transition-transform group-data-[state=open]:rotate-90" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-3 pt-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-1.5 block">Feature Name</Label>
                          <Input value={featureName} onChange={(e) => setFeatureName(e.target.value)} placeholder="e.g. User Onboarding" data-testid="feature-name-input"
                            className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] h-9 text-sm" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-1.5 block">Platform</Label>
                            <Select value={targetPlatform} onValueChange={setTargetPlatform}>
                              <SelectTrigger className="rounded-none border-[var(--pf-border)] h-9 text-sm" data-testid="platform-select"><SelectValue /></SelectTrigger>
                              <SelectContent className="rounded-none border-[var(--pf-border)]">{PLATFORMS.map((p) => (<SelectItem key={p.value} value={p.value} className="rounded-none text-sm">{p.label}</SelectItem>))}</SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-1.5 block">Output Detail</Label>
                            <Select value={outputMode} onValueChange={setOutputMode}>
                              <SelectTrigger className="rounded-none border-[var(--pf-border)] h-9 text-sm" data-testid="output-mode-select"><SelectValue /></SelectTrigger>
                              <SelectContent className="rounded-none border-[var(--pf-border)]">{OUTPUT_MODES.map((m) => (<SelectItem key={m.value} value={m.value} className="rounded-none text-sm">{m.label}</SelectItem>))}</SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </div>
              </div>
            )}

            {/* WORKSPACE with blocks: 3/9 grid */}
            {(hasPrompts || generating) && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ height: "calc(100vh - 7rem)" }}>
            {/* LEFT PANEL */}
            <div className="lg:col-span-3 overflow-hidden">
              <div className="sticky top-12 space-y-3 max-h-[calc(100vh-4rem)] overflow-y-auto">

                {/* GROUP 1: Navigate — Mode Tabs (always visible) */}
                {hasPrompts && (
                  <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)]" data-testid="mode-nav">
                    <div className="p-1">
                      {PROMPT_MODES.map(({ id, label, icon: Icon }) => {
                        const count = (selectedPrompt?.blocks?.[id] || []).length;
                        const isActive = activeMode === id;
                        const modeState = generatingModes[id];
                        const hasPlaybook = (selectedPrompt?.blocks?.[id] || []).some((b) => b.quality === "playbook");
                        return (
                          <button key={id} onClick={() => setActiveMode(id)} data-testid={`mode-${id}`}
                            className={`w-full flex items-center justify-between px-3 py-1.5 text-[13px] font-medium transition-colors duration-100 ${isActive ? "bg-[var(--pf-accent)] text-white" : "text-[var(--pf-text-secondary)] hover:bg-[var(--pf-surface)] hover:text-[var(--pf-text)]"}`}>
                            <span className="flex items-center gap-1.5">
                              {modeState === "loading" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
                              {label}
                            </span>
                            <span className="flex items-center gap-1">
                              {hasPlaybook && <span className="text-[9px] text-purple-400">{"{}"}</span>}
                              {modeState === "error" ? (
                                <span className="text-[9px] px-1 py-0.5 bg-red-500/20 text-red-400">!</span>
                              ) : count > 0 ? (
                                <span className={`text-[10px] px-1.5 py-0.5 leading-none ${isActive ? "bg-white/20 text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-muted)]"}`}>{modeState === "loading" ? "..." : count}</span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* GROUP 2: Actions (always visible when prompt exists) */}
                {hasPrompts && (
                  <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-3 space-y-2" data-testid="actions-panel">
                    <Button variant="outline" onClick={() => setLibraryOpen(true)} data-testid="browse-library-button"
                      className="w-full rounded-none border-[var(--pf-border)] h-8 text-xs hover:border-[var(--pf-accent)]">
                      <Library className="w-3 h-3 mr-1.5" />Pull from Library
                    </Button>
                    <Button variant="outline" onClick={() => setWorkflowStep("export")} data-testid="go-to-export"
                      className="w-full rounded-none border-[var(--pf-border)] h-8 text-xs hover:border-[var(--pf-accent)]">
                      <FileOutput className="w-3 h-3 mr-1.5" />Export Prompt
                    </Button>
                    <button onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters"
                      className={`w-full flex items-center justify-between px-3 py-1.5 text-xs font-medium transition-colors ${showFilters ? "text-[var(--pf-accent)]" : "text-[var(--pf-text-secondary)] hover:text-[var(--pf-text)]"}`}>
                      <span className="flex items-center gap-1.5"><Filter className="w-3 h-3" />Filters {activeFilters.length > 0 && `(${activeFilters.length})`}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? "rotate-180" : ""}`} />
                    </button>
                    {showFilters && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {activeFilters.length > 0 && <button onClick={() => setActiveFilters([])} className="text-[10px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)]" data-testid="clear-filters">Clear</button>}
                        {FILTER_TAGS.map((tag) => {
                          const isActive = activeFilters.includes(tag);
                          return <button key={tag} onClick={() => toggleFilter(tag)} data-testid={`filter-${tag.replace(/\s+/g, "-").toLowerCase()}`}
                            className={`text-[11px] px-2 py-1 transition-colors ${isActive ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>{tag}</button>;
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* GROUP 3: Advanced (collapsed) */}
                {hasPrompts && (
                  <Collapsible>
                    <CollapsibleTrigger className="w-full flex items-center justify-between px-4 py-2.5 border border-[var(--pf-border)] bg-[var(--pf-bg)] group" data-testid="advanced-panel-toggle">
                      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)]">Advanced</span>
                      <ChevronRight className="w-3 h-3 text-[var(--pf-text-muted)] transition-transform group-data-[state=open]:rotate-90" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border border-t-0 border-[var(--pf-border)] bg-[var(--pf-bg)] p-4 space-y-4">
                        {/* Concept */}
                        {selectedPrompt.concept && (
                          <div className="border-l-2 border-[var(--pf-accent)] bg-[var(--pf-surface)] px-3 py-2">
                            <p className="text-[10px] uppercase tracking-[0.15em] text-[var(--pf-text-muted)] mb-0.5">Concept</p>
                            <p className="text-xs text-[var(--pf-text-secondary)] font-mono line-clamp-3">{selectedPrompt.concept}</p>
                          </div>
                        )}
                        {/* Refinement Panel */}
                        <RefinementPanel promptData={selectedPrompt} onRefined={handleRefined} />
                        {/* New Generation */}
                        <Collapsible>
                          <CollapsibleTrigger className="flex items-center justify-between w-full group py-1.5 border-t border-[var(--pf-border)]" data-testid="regenerate-toggle">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--pf-text-muted)]">New Generation</span>
                            <ChevronRight className="w-3 h-3 text-[var(--pf-text-muted)] transition-transform group-data-[state=open]:rotate-90" />
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <form onSubmit={handleDirectGenerate} className="space-y-2 pt-2">
                              <Textarea value={concept} onChange={(e) => setConcept(e.target.value)} placeholder="New concept..." data-testid="concept-input"
                                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] resize-none font-mono text-xs" rows={3} />
                              <div className="grid grid-cols-2 gap-2">
                                <Select value={targetPlatform} onValueChange={setTargetPlatform}>
                                  <SelectTrigger className="rounded-none border-[var(--pf-border)] h-8 text-xs" data-testid="platform-select"><SelectValue /></SelectTrigger>
                                  <SelectContent className="rounded-none border-[var(--pf-border)]">{PLATFORMS.map((p) => (<SelectItem key={p.value} value={p.value} className="rounded-none text-xs">{p.label}</SelectItem>))}</SelectContent>
                                </Select>
                                <Select value={outputMode} onValueChange={setOutputMode}>
                                  <SelectTrigger className="rounded-none border-[var(--pf-border)] h-8 text-xs" data-testid="output-mode-select"><SelectValue /></SelectTrigger>
                                  <SelectContent className="rounded-none border-[var(--pf-border)]">{OUTPUT_MODES.map((m) => (<SelectItem key={m.value} value={m.value} className="rounded-none text-xs">{m.label}</SelectItem>))}</SelectContent>
                                </Select>
                              </div>
                              <Button type="submit" disabled={generating || !concept.trim()} data-testid="generate-button"
                                className="w-full bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-8 text-xs font-medium">
                                <Zap className="w-3 h-3 mr-1" />Quick Generate
                              </Button>
                            </form>
                          </CollapsibleContent>
                        </Collapsible>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* History */}
                {prompts.length > 1 && (
                  <Collapsible>
                    <CollapsibleTrigger className="w-full flex items-center justify-between px-4 py-2.5 border border-[var(--pf-border)] bg-[var(--pf-bg)] group" data-testid="history-toggle">
                      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)]">History ({prompts.length})</span>
                      <ChevronRight className="w-3 h-3 text-[var(--pf-text-muted)] transition-transform group-data-[state=open]:rotate-90" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border border-t-0 border-[var(--pf-border)] p-1.5 max-h-[160px] overflow-y-auto">
                        {prompts.map((p) => {
                          const blockCount = Object.values(p.blocks || {}).reduce((s, b) => s + b.length, 0);
                          const score = p.evaluation?.score;
                          return (
                            <button key={p.id} onClick={() => { setSelectedPrompt(p); setWorkflowStep(null); setVersionKey((k) => k + 1); }} data-testid={`history-item-${p.id}`}
                              className={`w-full text-left px-3 py-2 text-xs transition-colors duration-100 ${selectedPrompt?.id === p.id ? "bg-[var(--pf-accent-subtle)] text-[var(--pf-accent)]" : "text-[var(--pf-text-secondary)] hover:bg-[var(--pf-surface)]"}`}>
                              <p className="truncate font-medium">{p.feature_name || p.concept?.substring(0, 40)}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-[var(--pf-text-muted)] flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{new Date(p.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                                {blockCount > 0 && <span className="text-[10px] text-[var(--pf-text-muted)]">{blockCount} blocks</span>}
                                {score && <span className={`text-[10px] px-1 py-0.5 font-medium ${score >= 70 ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"}`}>{score}</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            </div>

            {/* RIGHT PANEL — Results */}
            <div className="lg:col-span-9 overflow-y-auto" style={{ maxHeight: "calc(100vh - 7rem)" }}>
              {generating && !isInWorkflow ? (
                <LoadingState messages={["Setting up your prompt workspace...", "Generating master prompt...", "Building block structure...", "Almost ready..."]} />
              ) : selectedPrompt ? (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="rounded-none bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] border-[var(--pf-border)] text-xs">{selectedPrompt.target_platform}</Badge>
                    {selectedPrompt.evaluation && (
                      <span className={`text-xs px-2 py-0.5 font-medium ${selectedPrompt.evaluation.score >= 80 ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : selectedPrompt.evaluation.score >= 60 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"}`} data-testid="prompt-quality-badge">Score: {selectedPrompt.evaluation.score}</span>
                    )}
                  </div>
                  <PromptResults promptData={selectedPrompt} activeMode={activeMode} activeFilters={activeFilters} onBlockUpdate={handleContentSaved} onModeBlocksReplaced={handleModeBlocksReplaced} standardBlocks={standardBlocks} generatingModes={generatingModes} />
                </div>
              ) : null}
            </div>
          </div>
            )}
          </>
        )}

        {/* Delete Project Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="rounded-none border-[var(--pf-border)] sm:max-w-sm">
<DialogBody>
            <DialogHeader><DialogTitle className="font-heading text-lg font-bold">Delete Playbook</DialogTitle></DialogHeader>
            <p className="text-sm text-[var(--pf-text-secondary)] mb-4">This will permanently delete &quot;{project?.name}&quot; and all its prompts.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1 rounded-none border-[var(--pf-border)] h-9">Cancel</Button>
              <Button onClick={handleDeleteProject} data-testid="confirm-delete-project" className="flex-1 rounded-none bg-red-600 hover:bg-red-700 text-white h-9">Delete</Button>
            </div>
</DialogBody>
</DialogContent>
        </Dialog>

        {/* Versions Sheet */}
        <Dialog open={versionsSheetOpen} onOpenChange={setVersionsSheetOpen}>
          <DialogContent className="rounded-none border-[var(--pf-border)] sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="versions-sheet">
<DialogBody>
            <DialogHeader>
              <DialogTitle className="font-heading text-lg font-bold flex items-center gap-2">
                <History className="w-4 h-4 text-[var(--pf-accent)]" />Prompt Versions
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-[var(--pf-text-secondary)] -mt-1">Every refinement creates a version. Restore any previous snapshot.</p>
            {selectedPrompt && (
              <div className="mt-3">
                <VersionTimeline key={versionKey} promptId={selectedPrompt.id} onRestored={(p) => { handleVersionRestored(p); setVersionsSheetOpen(false); }} />
              </div>
            )}
</DialogBody>
</DialogContent>
        </Dialog>

        {/* Pull from Library dialog (self-contained) */}
        <PullLibraryDialog
          open={libraryOpen}
          onOpenChange={setLibraryOpen}
          promptId={selectedPrompt?.id}
          onPulled={(updated) => { setSelectedPrompt(updated); setPrompts((prev) => prev.map((p) => (p.id === updated.id ? updated : p))); }}
        />

      </main>
      <Toaster position="bottom-right" toastOptions={{ className: "rounded-none border-[var(--pf-border)] bg-[var(--pf-bg)] text-[var(--pf-text)]" }} />
    </div>
  );
}
